/**
 * Thin wrappers around orval-generated Benchmarks hooks.
 *
 * Benchmark DATA is read-only — files are written to the bucket by the
 * ingest worker/exporter. The one mutation is the TRA-947 rename (title
 * only, never the slug). The list query polls while any benchmark is
 * processing, so an ingestion's card flips without a reload. Same unwrap
 * pattern as useEvals.ts (orval v8 FETCH client returns
 * {data, status, headers}).
 */

import {
  useQueryClient,
  type QueryKey,
  type UseMutationResult,
} from '@tanstack/react-query'
import { toast } from 'sonner'

import { type ApiRequestError, getApiErrorDetail } from '@/api/client'
import type {
  getBenchmarkApiBenchmarksBenchmarkSlugGetResponse,
  listBenchmarksApiBenchmarksGetResponse,
} from '@/api/generated/benchmarks/benchmarks'
import {
  getGetBenchmarkApiBenchmarksBenchmarkSlugGetQueryKey,
  getListBenchmarksApiBenchmarksGetQueryKey,
  useGetBenchmarkApiBenchmarksBenchmarkSlugGet,
  useRenameBenchmarkApiBenchmarksBenchmarkSlugPatch,
  useGetRolloutApiBenchmarksBenchmarkSlugRolloutsTaskSlugModelRolloutIdGet,
  useGetTaskProcessApiBenchmarksBenchmarkSlugProcessTaskSlugGet,
  useListBenchmarksApiBenchmarksGet,
  useListRolloutsApiBenchmarksBenchmarkSlugRolloutsTaskSlugGet,
} from '@/api/generated/benchmarks/benchmarks'
import type {
  BenchmarkDetailResponse,
  BenchmarkRename,
  BenchmarksListResponse,
  RolloutDetailResponse,
  RolloutListResponse,
  TaskProcessResponse,
} from '@/api/generated/model'

/**
 * Cached shapes for the optimistic rename. The orval FETCH client caches the
 * whole {status, data, headers} envelope, so a cache write has to preserve it
 * — and `undefined` is legitimate (nothing fetched that key yet).
 */
type ListQueryData = listBenchmarksApiBenchmarksGetResponse | undefined
type DetailQueryData = getBenchmarkApiBenchmarksBenchmarkSlugGetResponse | undefined

interface RenameSnapshot {
  list: ListQueryData
  detail: DetailQueryData
  listKey: QueryKey
  detailKey: QueryKey
}

interface UnwrappedQuery<TData> {
  data: TData | undefined
  isLoading: boolean
  error: ApiRequestError | null
  isError: boolean
}

/** Poll the list while any card is processing so it flips without a reload. */
const PROCESSING_REFETCH_MS = 15_000

export function useBenchmarksList(): UnwrappedQuery<BenchmarksListResponse> {
  const query = useListBenchmarksApiBenchmarksGet({
    query: {
      refetchInterval: (current) => {
        const body = current.state.data
        const benchmarks = body?.status === 200 ? body.data.benchmarks : []
        return benchmarks.some((b) => b.status === 'processing')
          ? PROCESSING_REFETCH_MS
          : false
      },
    },
  })
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}

/** Pure cache transform: retitle one card in the cached list envelope. */
function patchListTitle(
  slug: string,
  name: string
): (current: ListQueryData) => ListQueryData {
  return (current: ListQueryData): ListQueryData => {
    if (current?.status !== 200) return current
    return {
      ...current,
      data: {
        benchmarks: current.data.benchmarks.map((benchmark) =>
          benchmark.slug === slug ? { ...benchmark, display_name: name } : benchmark
        ),
      },
    }
  }
}

/** Pure cache transform: retitle the cached detail envelope's manifest. */
function patchDetailTitle(name: string): (current: DetailQueryData) => DetailQueryData {
  return (current: DetailQueryData): DetailQueryData => {
    if (current?.status !== 200) return current
    return {
      ...current,
      data: {
        ...current.data,
        manifest: { ...current.data.manifest, display_name: name },
      },
    }
  }
}

async function snapshotAndPatch(
  queryClient: ReturnType<typeof useQueryClient>,
  slug: string,
  name: string
): Promise<RenameSnapshot> {
  const listKey = getListBenchmarksApiBenchmarksGetQueryKey()
  const detailKey = getGetBenchmarkApiBenchmarksBenchmarkSlugGetQueryKey(slug)
  // In-flight refetches would land after this write and undo it.
  await queryClient.cancelQueries({ queryKey: listKey })
  await queryClient.cancelQueries({ queryKey: detailKey })
  const snapshot: RenameSnapshot = {
    list: queryClient.getQueryData(listKey),
    detail: queryClient.getQueryData(detailKey),
    listKey,
    detailKey,
  }
  queryClient.setQueryData(listKey, patchListTitle(slug, name))
  queryClient.setQueryData(detailKey, patchDetailTitle(name))
  return snapshot
}

/**
 * Rename a benchmark (TRA-947), applied optimistically.
 *
 * A rename is a pure relabel the server cannot reject on content, so the new
 * title goes into the cache immediately and the dialog closes on the spot.
 * Waiting for the round-trip would be a visible stall: the PATCH rewrites
 * manifest.json in GCS, and the list refetch behind it re-reads a manifest
 * AND a curve object per benchmark. Rolled back and surfaced if the server
 * disagrees.
 */
export function useRenameBenchmark(): UseMutationResult<
  unknown,
  ApiRequestError,
  { benchmarkSlug: string; data: BenchmarkRename },
  RenameSnapshot
> {
  const queryClient = useQueryClient()
  return useRenameBenchmarkApiBenchmarksBenchmarkSlugPatch<
    ApiRequestError,
    RenameSnapshot
  >({
    mutation: {
      onMutate: (variables) =>
        snapshotAndPatch(queryClient, variables.benchmarkSlug, variables.data.display_name),
      onError: (error, _variables, snapshot) => {
        // Put the old title back rather than leaving a lie on screen, and
        // say why — the dialog is long closed by now.
        if (snapshot !== undefined) {
          queryClient.setQueryData(snapshot.listKey, snapshot.list)
          queryClient.setQueryData(snapshot.detailKey, snapshot.detail)
        }
        toast.error(getApiErrorDetail(error) ?? 'Rename failed. Try again')
      },
      onSettled: (_result, _error, variables) => {
        void queryClient.invalidateQueries({
          queryKey: getListBenchmarksApiBenchmarksGetQueryKey(),
        })
        void queryClient.invalidateQueries({
          queryKey: getGetBenchmarkApiBenchmarksBenchmarkSlugGetQueryKey(
            variables.benchmarkSlug
          ),
        })
      },
    },
  })
}

export function useBenchmark(slug: string | undefined): UnwrappedQuery<BenchmarkDetailResponse> {
  const query = useGetBenchmarkApiBenchmarksBenchmarkSlugGet(slug ?? '', {
    query: { enabled: !!slug },
  })
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}

export function useRolloutList(
  benchmarkSlug: string | undefined,
  taskSlug: string | undefined
): UnwrappedQuery<RolloutListResponse> {
  const query = useListRolloutsApiBenchmarksBenchmarkSlugRolloutsTaskSlugGet(
    benchmarkSlug ?? '',
    taskSlug ?? '',
    { query: { enabled: !!benchmarkSlug && !!taskSlug } }
  )
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}

/**
 * The task's golden-process analysis. 404 is a legitimate state (the
 * benchmark ships no analysis for this task) — callers read it via
 * getApiErrorStatus(error) and hide the timeline.
 */
export function useTaskProcess(
  benchmarkSlug: string | undefined,
  taskSlug: string | undefined
): UnwrappedQuery<TaskProcessResponse> {
  const query = useGetTaskProcessApiBenchmarksBenchmarkSlugProcessTaskSlugGet(
    benchmarkSlug ?? '',
    taskSlug ?? '',
    { query: { enabled: !!benchmarkSlug && !!taskSlug, retry: false } }
  )
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}

export function useRollout(
  benchmarkSlug: string | undefined,
  taskSlug: string | undefined,
  model: string | undefined,
  rolloutId: string | undefined
): UnwrappedQuery<RolloutDetailResponse> {
  const query = useGetRolloutApiBenchmarksBenchmarkSlugRolloutsTaskSlugModelRolloutIdGet(
    benchmarkSlug ?? '',
    taskSlug ?? '',
    model ?? '',
    rolloutId ?? '',
    { query: { enabled: !!benchmarkSlug && !!taskSlug && !!model && !!rolloutId } }
  )
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}
